import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useId, useState } from "react";
import { notifyError } from "../../utils/notifications";
import { LeafletHeatmap } from "./LeafletHeatmap";
import { VesselsModal } from "./VesselsModal";
import { WorldMapLegend } from "./WorldMapLegend";
import { handleViewportChange, mapLocationsToTuples, } from "./heatmapHelper";
const MAP_OPTIONS = {
    minZoom: 2,
    maxZoom: 11,
    initialZoomLevel: 2,
    attributionControl: true,
    dragging: true,
    doubleClickZoom: true,
    scrollWheelZoom: true,
    zoomControl: true,
    markerMinZoomLevel: -10,
    zoneMinZoomLevel: 6,
    showLocations: true,
    showMarkersOnlyOnHover: true,
    locationLinkTo: "RegionalStats",
    initialCenter: [25, 0],
    maxBounds: [
        [-90, -180],
        [90, 180],
    ],
};
const getFallbackViewport = () => ({
    zoomLevel: MAP_OPTIONS.initialZoomLevel,
    northWest: {
        lat: MAP_OPTIONS.maxBounds[1][0],
        lng: MAP_OPTIONS.maxBounds[0][1],
    },
    southEast: {
        lat: MAP_OPTIONS.maxBounds[0][0],
        lng: MAP_OPTIONS.maxBounds[1][1],
    },
});
export const WorldMap = ({ options }) => {
    const [userMode, setUserMode] = useState(true);
    const [ncVesselsList, setNcVesselsList] = useState([]);
    const [userData, setUserData] = useState([]);
    const [industryData, setIndustryData] = useState([]);
    const [locationData, setLocationData] = useState([]);
    const mapId = useId().replace(/:/g, "");
    const [viewport, setViewport] = useState({ zoomLevel: MAP_OPTIONS.initialZoomLevel });
    const ncPoints = userMode ? userData : industryData;
    const reloadIndustryData = async (newOptions, currentViewport, isUserMode) => {
        if (!newOptions?.isReady || isUserMode) {
            return;
        }
        const effectiveViewport = currentViewport.northWest && currentViewport.southEast ? currentViewport : getFallbackViewport();
        const northWest = effectiveViewport.northWest;
        const southEast = effectiveViewport.southEast;
        if (!northWest || !southEast) {
            return;
        }
        const params = new URLSearchParams({
            fromMonth: newOptions.startDate.substring(0, 7),
            toMonth: newOptions.endDate.substring(0, 7),
            vesselTypes: newOptions.vesselTypes.join(","),
            zoomLevel: String(effectiveViewport.zoomLevel),
            maxLatitude: String(northWest.lat),
            maxLongitude: String(southEast.lng),
            minLatitude: String(southEast.lat),
            minLongitude: String(northWest.lng),
        });
        try {
            const response = await fetch(`/api/benchmark/industryheatmap?${params}`);
            if (!response.ok) {
                throw new Error(response.statusText);
            }
            setIndustryData(await response.json());
        }
        catch {
            notifyError("An error has occurred when loading stats. Please try again.");
        }
    };
    const reloadLocations = async (newOptions, isUserMode) => {
        if (!newOptions?.isReady) {
            return;
        }
        const params = new URLSearchParams({
            fromMonth: newOptions.startDate.substring(0, 7),
            toMonth: newOptions.endDate.substring(0, 7),
            fleets: newOptions.fleet.map((fleet) => fleet.ID).join(","),
            vesselTypes: newOptions.vesselTypes.join(","),
            isUserData: String(isUserMode),
        });
        try {
            const response = await fetch(`/api/benchmark/portperformances?${params}`);
            if (!response.ok) {
                throw new Error(response.statusText);
            }
            const locations = await response.json();
            setLocationData(mapLocationsToTuples(locations));
        }
        catch {
            notifyError("An error has occurred when loading stats. Please try again.");
        }
    };
    const reloadNcVessels = async (newOptions, currentViewport, isUserMode) => {
        if (!newOptions?.isReady || !currentViewport.northWest || !currentViewport.southEast || !isUserMode) {
            return;
        }
        setNcVesselsList([]);
        const params = new URLSearchParams({
            fromMonth: newOptions.startDate.substring(0, 7),
            toMonth: newOptions.endDate.substring(0, 7),
            fleets: newOptions.fleet.map((fleet) => fleet.ID).join(","),
            minLatitude: String(currentViewport.southEast.lat),
            maxLatitude: String(currentViewport.northWest.lat),
            minLongitude: String(currentViewport.northWest.lng),
            maxLongitude: String(currentViewport.southEast.lng),
        });
        try {
            const response = await fetch(`/api/benchmark/userncvessels?${params}`);
            if (!response.ok) {
                throw new Error(response.statusText);
            }
            const vessels = await response.json();
            setNcVesselsList(vessels);
        }
        catch {
            setNcVesselsList([]);
            notifyError("An error has occurred when loading NC vessel list. Please try again.");
        }
    };
    useEffect(() => {
        if (!options.isReady)
            return;
        const params = new URLSearchParams({
            fromMonth: options.startDate.substring(0, 7),
            toMonth: options.endDate.substring(0, 7),
            fleets: options.fleet.map((fleet) => fleet.ID).join(","),
        });
        fetch(`/api/benchmark/userheatmap?${params}`)
            .then((response) => {
            if (response.ok) {
                return response.json();
            }
            throw new Error(response.statusText);
        })
            .then((stats) => {
            setUserData(stats);
        })
            .catch(() => {
            notifyError("An error has occurred when loading stats. Please try again.");
        });
    }, [options, options.isReady, options.startDate, options.endDate, options.fleet.map((fleet) => fleet.ID).join(",")]);
    useEffect(() => {
        void reloadLocations(options, userMode);
    }, [
        options,
        options.isReady,
        options.startDate,
        options.endDate,
        options.vesselTypes.join(","),
        options.fleet.map((fleet) => fleet.ID).join(","),
        userMode,
    ]);
    useEffect(() => {
        void reloadIndustryData(options, viewport, userMode);
    }, [
        options,
        options.isReady,
        options.startDate,
        options.endDate,
        options.vesselTypes.join(","),
        options.fleet.map((fleet) => fleet.ID).join(","),
        userMode,
        viewport.northWest?.lat,
        viewport.northWest?.lng,
        viewport.southEast?.lat,
        viewport.southEast?.lng,
        viewport.zoomLevel,
    ]);
    useEffect(() => {
        void reloadNcVessels(options, viewport, userMode);
    }, [
        options,
        options.isReady,
        options.startDate,
        options.endDate,
        options.vesselTypes.join(","),
        options.fleet.map((fleet) => fleet.ID).join(","),
        userMode,
        viewport.northWest?.lat,
        viewport.northWest?.lng,
        viewport.southEast?.lat,
        viewport.southEast?.lng,
        viewport.zoomLevel,
    ]);
    return (_jsxs("div", { className: "position-relative card shadow", children: [_jsx(LeafletHeatmap, { id: mapId, options: MAP_OPTIONS, data: ncPoints, locations: locationData, params: options, onViewportChange: (nextViewport) => handleViewportChange(nextViewport, setViewport), className: "w-100 mb-1 card-body", style: { height: "530px" } }), _jsx(ModeToggleButton, { userMode: userMode, showUserMode: () => setUserMode(true), showIndustryMode: () => setUserMode(false) }), _jsx(WorldMapLegend, { userMode: userMode }), _jsx(VesselsModal, { ncVesselsList: ncVesselsList })] }));
};
const ModeToggleButton = ({ userMode, showUserMode, showIndustryMode, }) => {
    return (_jsx("div", { style: { top: 0, zIndex: 500 }, className: "position-absolute m-3", children: _jsxs("div", { className: "btn-group mb-3", role: "group", "aria-label": "Data selector", children: [_jsx("button", { type: "button", className: `btn ${userMode ? "btn-primary" : "bg-white btn-outline-primary"}`, onClick: showUserMode, children: "View only your fleet" }), _jsx("button", { type: "button", className: `btn ${!userMode ? "btn-primary" : "bg-white btn-outline-primary"}`, onClick: showIndustryMode, children: "View industry data" })] }) }));
};
